import React from 'react';

import type { AvailableIconName } from '@/assets/icons';
import { Scroll, Stack } from '@/components/layout';
import { Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { guardianColors } from '@/theme/colors';
import { ActionRow } from './action-row';
import { BiometricRow } from './biometric-row';
import { SecuritySection } from './security-section';
import { SocialAccountRow } from './social-account-row';

const strings = STRINGS.guardianProfile.accountSecurities;

type AccountSecuritiesBiometric = {
  icon: AvailableIconName;
  title: string;
  subtitle: string;
  enabled: boolean;
  disabled: boolean;
  onValueChange: (value: boolean) => void;
};

type AccountSecuritiesContentProps = {
  tabletPanel?: boolean;
  biometric?: AccountSecuritiesBiometric;
  appleConnected: boolean;
  googleConnected: boolean;
  onChangePassword: () => void;
};

export function AccountSecuritiesContent({
  tabletPanel = false,
  biometric,
  appleConnected,
  googleConnected,
  onChangePassword,
}: AccountSecuritiesContentProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Scroll
      style={styles.scroll}
      contentContainerStyle={styles.content}
      backgroundColor={guardianColors['1']}
      showsVerticalScrollIndicator={false}>
      <Stack style={tabletPanel ? styles.tabletPanel : styles.phonePanel}>
        {tabletPanel ? (
          <Text variant="Font Guardian App/Heading/Heading H5 24px" style={styles.panelTitle}>
            {strings.title}
          </Text>
        ) : null}

        <SecuritySection title={strings.sections.security}>
          <ActionRow
            icon="IcSecurityUser"
            label={strings.changePassword}
            onPress={onChangePassword}
          />
        </SecuritySection>

        {biometric ? (
          <SecuritySection title={strings.sections.biometric} style={styles.sectionAfter}>
            <BiometricRow
              icon={biometric.icon}
              title={biometric.title}
              subtitle={biometric.subtitle}
              value={biometric.enabled}
              disabled={biometric.disabled}
              onValueChange={biometric.onValueChange}
            />
          </SecuritySection>
        ) : null}

        <SecuritySection title={strings.sections.socialAccounts} style={styles.sectionAfter}>
          <Stack gap={scale({ phone: 12, tablet: 16 })}>
            <SocialAccountRow
              icon="IcApple"
              title={strings.appleAccount}
              subtitle={strings.appleSubtitle}
              connected={appleConnected}
              darkIcon
            />
            <SocialAccountRow
              icon="IcGoogle"
              title={strings.googleAccount}
              subtitle={strings.googleSubtitle}
              connected={googleConnected}
            />
          </Stack>
        </SecuritySection>
      </Stack>
    </Scroll>
  );
}

const useStyles = createStyles(({ scale }) => ({
  scroll: { flex: 1 },
  content: {
    flexGrow: 1,
    paddingTop: scale({ phone: 12, tablet: 16 }),
    paddingHorizontal: scale({ phone: 16, tablet: 20 }),
  },
  phonePanel: {
    flex: 1,
    alignSelf: 'stretch',
  },
  tabletPanel: {
    flex: 1,
    alignSelf: 'stretch',
  },
  panelTitle: {
    color: guardianColors['10'],
    marginBottom: scale(20),
  },
  sectionAfter: {
    marginTop: scale(20),
  },
}));
